import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as packagesApi from './packages.api';

import type {
  CreatePackageBatchRequest,
  CreatePackageDayRequest,
  CreatePackageFaqRequest,
  CreatePackageFlightRequest,
  CreatePackageHotelRequest,
  CreatePackageImageRequest,
  CreatePackageRequest,
  CreatePackageVariantRequest,
  PackageListQuery,
  SetPackageCollectionsRequest,
  SetPackageExclusionsRequest,
  SetPackageHeroInclusionsRequest,
  SetPackageInclusionsRequest,
  UpdatePackageBatchRequest,
  UpdatePackageDayRequest,
  UpdatePackageFaqRequest,
  UpdatePackageFlightRequest,
  UpdatePackageHotelRequest,
  UpdatePackageImageRequest,
  UpdatePackageRequest,
  UpdatePackageVariantRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: PackageListQuery) => ['packages', 'list', query] as const,
  detail: (id: string) => ['packages', 'detail', id] as const,
};

export const usePackagesList = (query: PackageListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => packagesApi.listPackages(query),
    staleTime: 30_000,
  });

export const usePackageDetail = (id: string | undefined) =>
  useQuery({
    queryKey: id ? QK.detail(id) : ['packages', 'detail', 'none'],
    queryFn: () => packagesApi.getPackage(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

export const useCreatePackage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePackageRequest) => packagesApi.createPackage(payload),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success(`Added ${p.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add package'),
  });
};

export const useUpdatePackage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdatePackageRequest }) =>
      packagesApi.updatePackage(id, payload),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(p.id) });
      toast.success('Package updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update package'),
  });
};

export const useDeletePackage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => packagesApi.deletePackage(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Package deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete package'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};

// ── Days ──────────────────────────────────────────────────────────────────────

export const useAddPackageDay = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePackageDayRequest) =>
      packagesApi.createPackageDay(packageId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Day added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add day'),
  });
};

export const useUpdatePackageDay = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ childId, payload }: { childId: string; payload: UpdatePackageDayRequest }) =>
      packagesApi.updatePackageDay(packageId, childId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Day updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update day'),
  });
};

export const useDeletePackageDay = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (childId: string) => packagesApi.deletePackageDay(packageId, childId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Day removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove day'),
  });
};

// ── Hotels ────────────────────────────────────────────────────────────────────

export const useAddPackageHotel = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePackageHotelRequest) =>
      packagesApi.createPackageHotel(packageId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Hotel added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add hotel'),
  });
};

export const useUpdatePackageHotel = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ childId, payload }: { childId: string; payload: UpdatePackageHotelRequest }) =>
      packagesApi.updatePackageHotel(packageId, childId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Hotel updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update hotel'),
  });
};

export const useDeletePackageHotel = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (childId: string) => packagesApi.deletePackageHotel(packageId, childId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Hotel removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove hotel'),
  });
};

// ── Flights ───────────────────────────────────────────────────────────────────

export const useAddPackageFlight = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePackageFlightRequest) =>
      packagesApi.createPackageFlight(packageId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Flight added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add flight'),
  });
};

export const useUpdatePackageFlight = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ childId, payload }: { childId: string; payload: UpdatePackageFlightRequest }) =>
      packagesApi.updatePackageFlight(packageId, childId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Flight updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update flight'),
  });
};

export const useDeletePackageFlight = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (childId: string) => packagesApi.deletePackageFlight(packageId, childId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Flight removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove flight'),
  });
};

// ── FAQs ──────────────────────────────────────────────────────────────────────

export const useAddPackageFaq = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePackageFaqRequest) =>
      packagesApi.createPackageFaq(packageId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('FAQ added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add FAQ'),
  });
};

export const useUpdatePackageFaq = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ childId, payload }: { childId: string; payload: UpdatePackageFaqRequest }) =>
      packagesApi.updatePackageFaq(packageId, childId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('FAQ updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update FAQ'),
  });
};

export const useDeletePackageFaq = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (childId: string) => packagesApi.deletePackageFaq(packageId, childId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('FAQ removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove FAQ'),
  });
};

// ── Images ────────────────────────────────────────────────────────────────────

export const useAddPackageImage = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePackageImageRequest) =>
      packagesApi.createPackageImage(packageId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Image added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add image'),
  });
};

export const useUpdatePackageImage = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ childId, payload }: { childId: string; payload: UpdatePackageImageRequest }) =>
      packagesApi.updatePackageImage(packageId, childId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Image updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update image'),
  });
};

export const useDeletePackageImage = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (childId: string) => packagesApi.deletePackageImage(packageId, childId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Image removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove image'),
  });
};

// ── Batches & variants ────────────────────────────────────────────────────────

export const useAddPackageBatch = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePackageBatchRequest) =>
      packagesApi.createPackageBatch(packageId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Batch added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add batch'),
  });
};

export const useUpdatePackageBatch = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ childId, payload }: { childId: string; payload: UpdatePackageBatchRequest }) =>
      packagesApi.updatePackageBatch(packageId, childId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Batch updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update batch'),
  });
};

export const useDeletePackageBatch = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (childId: string) => packagesApi.deletePackageBatch(packageId, childId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Batch removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove batch'),
  });
};

export const useAddPackageVariant = (packageId: string, batchId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePackageVariantRequest) =>
      packagesApi.createPackageVariant(packageId, batchId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Variant added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add variant'),
  });
};

export const useUpdatePackageVariant = (packageId: string, batchId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ childId, payload }: { childId: string; payload: UpdatePackageVariantRequest }) =>
      packagesApi.updatePackageVariant(packageId, batchId, childId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Variant updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update variant'),
  });
};

export const useDeletePackageVariant = (packageId: string, batchId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (childId: string) => packagesApi.deletePackageVariant(packageId, batchId, childId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Variant removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove variant'),
  });
};

// ── Bulk-set: Inclusions / Exclusions / Collections ───────────────────────────

export const useSetPackageInclusions = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SetPackageInclusionsRequest) =>
      packagesApi.setPackageInclusions(packageId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Inclusions saved');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to save inclusions'),
  });
};

export const useSetPackageExclusions = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SetPackageExclusionsRequest) =>
      packagesApi.setPackageExclusions(packageId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Exclusions saved');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to save exclusions'),
  });
};

export const useSetPackageCollections = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SetPackageCollectionsRequest) =>
      packagesApi.setPackageCollections(packageId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Collections saved');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to save collections'),
  });
};

export const useSetPackageHeroInclusions = (packageId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SetPackageHeroInclusionsRequest) =>
      packagesApi.setPackageHeroInclusions(packageId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(packageId) });
      qc.invalidateQueries({ queryKey: ['packages', 'list'] });
      toast.success('Highlights saved');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to save highlights'),
  });
};
